import {
  MAX_TOOL_CHAIN_DEPTH,
  TOOL_RUN_MODES,
  inspectToolChainDepth,
  isToolName,
  toolCallSchema,
  toolChainSchema,
  type ToolName,
} from '@galapagos/shared/src/flow/tools';
// Keep the headless runtime closure free of the shared barrel's browser providers.
import { isUnknownRecord as isRecord } from '@galapagos/shared/src/validation/unknown-record';
import { preflightToolChain, type ToolHandlers } from './toolPreflight';

export const JSON_RENDER_TOOL_CHAIN_HANDLER = '__galapagos_dispatch_tool_chain';

type JsonRenderToolBinding = {
  action?: string;
  tool_name?: string;
  parameters?: Record<string, unknown>;
  params?: Record<string, unknown>;
  confirm?: unknown;
  onSuccess?: unknown;
  onError?: unknown;
  preventDefault?: boolean;
};

type JsonRenderToolSpec = {
  elements: Record<string, unknown>;
};

export interface ExtractedJsonRenderToolChain {
  elementKey: string;
  eventName: string;
  tools: unknown[];
}

export type JsonRenderToolChainValidationResult =
  | { success: true }
  | { success: false; error: Error };

export function normaliseJsonRenderToolChains<TSpec extends JsonRenderToolSpec>(
  spec: TSpec,
): TSpec {
  const elements = Object.fromEntries(
    Object.entries(spec.elements).map(([key, element]) => [key, normaliseElement(element)]),
  );

  return { ...spec, elements };
}

/** Extract every catalog tool chain from one renderable spec without executing it. */
export function extractJsonRenderToolChains(
  spec: JsonRenderToolSpec,
): ExtractedJsonRenderToolChain[] {
  const chains: ExtractedJsonRenderToolChain[] = [];

  for (const [elementKey, element] of Object.entries(spec.elements)) {
    if (!isRecord(element)) continue;

    if (isRecord(element.on)) {
      for (const [eventName, binding] of Object.entries(element.on)) {
        const tools = toolChainForEventBinding(binding);
        if (!tools) continue;
        chains.push({ elementKey, eventName, tools });
      }
    }

    // `watch` maps a state path (not a UI event) to a tool chain that fires
    // whenever the value at that path changes — same binding shape as `on`,
    // so it reuses the identical extraction/normalization/validation path.
    if (isRecord(element.watch)) {
      for (const [statePath, binding] of Object.entries(element.watch)) {
        const tools = toolChainForEventBinding(binding);
        if (!tools) continue;
        chains.push({ elementKey, eventName: `watch:${statePath}`, tools });
      }
    }
  }

  return chains;
}

/**
 * Validate all supplied renderable specs and handler bindings as one boundary.
 * Callers pass only their visible specs; failure means none should be rendered.
 */
export function preflightJsonRenderToolSpecs(
  specs: readonly JsonRenderToolSpec[],
  handlers: ToolHandlers,
): JsonRenderToolChainValidationResult {
  for (const [specIndex, spec] of specs.entries()) {
    for (const chain of extractJsonRenderToolChains(spec)) {
      const result = preflightToolChain(chain.tools, handlers);
      if (!result.ok) {
        return {
          success: false,
          error: new Error(
            `Spec ${specIndex + 1} element "${chain.elementKey}" ${chain.eventName} tool chain failed preflight: ${result.error.message}`,
          ),
        };
      }
    }
  }

  return { success: true };
}

export function validateJsonRenderToolChains(
  spec: JsonRenderToolSpec,
): JsonRenderToolChainValidationResult {
  for (const { elementKey, eventName, tools } of extractJsonRenderToolChains(spec)) {
    const inspection = inspectToolChainDepth(tools);
    if (!inspection.ok) {
      return {
        success: false,
        error: new Error(
          `Element "${elementKey}" ${eventName} tool chain ${toolTreeInspectionMessage(
            inspection,
          )}`,
        ),
      };
    }

    const result = toolChainSchema.safeParse(tools);
    if (!result.success) {
      return {
        success: false,
        error: new Error(`Element "${elementKey}" ${eventName} tool chain failed validation`),
      };
    }
  }

  return { success: true };
}

/**
 * The tool names an event binding references that this build's catalog does not
 * carry — the input to the tolerant resolve pass's tool handling (ADR-118 Rule 8).
 *
 * Shares `toolChainForEventBinding` with normalization and validation on purpose:
 * "is this event a Galapagos tool binding at all" must have exactly one answer
 * across the three. A renderer-owned binding (no `tool_name` anywhere, and no
 * `action` naming a catalogued tool) yields no names, so the resolver leaves it
 * alone rather than degrading UI the host never owned.
 *
 * A mixed event — one catalogued tool beside a renderer-local `action` — yields
 * the renderer-local name as unknown, so the whole binding degrades. That is
 * ADR-046 Rule 15's fail-closed posture, not an accident of the walk: the event
 * is one dispatcher chain, and half a chain must never run.
 *
 * Ordered cheapest-first, and that ordering is a contract with the spec's
 * performance budget, not a micro-optimisation. On the clean path — zero unknown
 * tools, which is every home adoption today — this must cost a set-membership
 * check per authored binding and nothing more. It previously converted the whole
 * binding, walked the conversion, and zod-parsed every known call before reaching
 * the scan that returns `[]`, on every binding of every document.
 *
 * So the name scan runs FIRST, over the authored tree, and the expensive screens
 * run only once an unknown name has actually been found. Names resolve
 * identically either side of conversion — both go through `getToolName`, which
 * takes `action` as an equal spelling — so reading the authored tree changes
 * nothing about which names are reported.
 *
 * The scan is therefore self-guarded against depth and cycles, because nothing
 * has screened the binding when it runs: the resolve pass executes BEFORE
 * catalog-stage validation, so a cyclic branch object reaches this walk while it
 * is still the document's problem and not yet a rejected document.
 */
export function unknownToolNamesInBinding(
  binding: unknown,
  isKnownToolName: (name: string) => boolean,
): string[] {
  const authored = bindingsForEvent(binding);
  if (!isToolChainBinding(authored)) return [];

  const unknown: string[] = [];
  const seen = new WeakSet<object>();

  const visitChain = (calls: unknown, depth: number): void => {
    if (!Array.isArray(calls) || depth > MAX_TOOL_CHAIN_DEPTH) return;
    if (seen.has(calls)) return;
    seen.add(calls);

    for (const call of calls) {
      if (!isRecord(call)) continue;
      if (seen.has(call)) continue;
      seen.add(call);

      const name = getToolName(call);
      if (typeof name === 'string' && !isKnownToolName(name) && !unknown.includes(name)) {
        unknown.push(name);
      }

      for (const branch of ['onSuccess', 'onError'] as const) {
        visitChain(call[branch], depth + 1);
      }
    }
  };

  visitChain(authored, 1);
  if (unknown.length === 0) return [];

  // A binding that is malformed for ANY reason besides an unknown name is not
  // this pass's business, and saying so is what stops tolerance becoming a
  // validation bypass. Reporting an unknown name deletes the whole binding, so
  // a binding that ALSO exceeds the depth cap, contains a cycle, carries a
  // known tool with parameters its schema rejects, or is simply the wrong shape
  // would vanish — and the document would validate where it used to fail the
  // catalog stage. Reporting nothing leaves the binding in place for
  // `validateJsonRenderToolChains` to reject on its own terms, which is the
  // outcome such a document already earned.
  //
  // Both screens read the AUTHORED binding. `inspectToolChainDepth` walks
  // `onSuccess`/`onError` arrays and accepts the authored shape unchanged, and
  // `toToolCall` projects each call onto {tool_name, parameters, branches} and
  // discards everything else — so an imperative `if`/`when`/`cond`/`loop` is
  // already gone by the time a converted chain exists, and a check reading the
  // projection would pronounce it clean and strip the binding. That is the
  // bypass these screens were added to close.
  if (!inspectToolChainDepth(authored).ok) return [];
  if (!isOtherwiseWellFormed(authored, isKnownToolName)) return [];

  return unknown;
}

/**
 * Every field an author may legitimately put on a tool call.
 *
 * The union of what `toolCallSchema` models and what the bridge lifts off the
 * binding itself. One list, read by both branches of the well-formedness check,
 * because keeping two was exactly how the known-call path came to reject
 * `confirm` after the unknown-call path had been fixed to accept it.
 */
const AUTHORABLE_CALL_FIELDS = new Set([
  'tool_name',
  'action',
  'parameters',
  'params',
  'onSuccess',
  'onError',
  'preventDefault',
  'confirm',
]);

/** The subset `toolCallSchema` itself models — anything else is bridge-only. */
const SCHEMA_MODELLED_CALL_FIELDS = new Set(['tool_name', 'parameters']);

/**
 * Is every call in this tree well-formed apart from its unknown names?
 *
 * A KNOWN call is held to its full catalog contract — the same
 * `toolCallSchema` the strict validator applies, minus the branches, which are
 * walked separately. An UNKNOWN call has no arm to validate against, so it is
 * held to the shape every arm shares: an object `parameters`, and array-or-
 * absent branches. Anything else and this binding is malformed independently of
 * the unknown name, so tolerance declines it.
 */
function isOtherwiseWellFormed(
  authored: readonly unknown[],
  isKnownToolName: (name: string) => boolean,
): boolean {
  const seen = new WeakSet<object>();

  const wellFormedCall = (call: unknown): boolean => {
    if (!isRecord(call)) return false;
    if (seen.has(call)) return true;
    seen.add(call);

    for (const field of ['onSuccess', 'onError'] as const) {
      const branch = call[field];
      if (branch === undefined) continue;
      if (!Array.isArray(branch)) return false;
      if (!branch.every(wellFormedCall)) return false;
    }

    const name = getToolName(call);
    if (typeof name !== 'string') return false;

    if (!isKnownToolName(name)) {
      // An unknown name has no catalog arm to validate against, so it is held to
      // the shape every arm shares — INCLUDING `.strict()`'s refusal of extra
      // keys. Checking only `parameters` let `{ tool_name: 'unknown',
      // parameters: {}, if: 'x' }` read as merely-unknown, get its binding
      // stripped, and the document validate, where the strict union rejects it:
      // tolerance became a bypass for a malformed binding rather than a
      // narrowing to the unknown-name case.
      //
      // An allowlist, not a denylist of `if`/`when`/`cond`/`loop`: the point is
      // parity with `.strict()`'s REFUSAL OF CONTROL FLOW, and a denylist would
      // admit the next imperative field somebody invents.
      //
      // `confirm` and `preventDefault` are allowed. Both are absent from
      // `toolCallSchema`, but absence there is not the test — the bridge lifts
      // both onto the dispatch binding in `normaliseEventBinding`, and
      // `toToolCall` discards them before `.strict()` ever runs, so a KNOWN tool
      // carrying either validates today. Refusing them here would mean an
      // authored `confirm` beside an unknown tool declines tolerance and takes
      // the WHOLE document to the bundled fallback — the exact defect this pass
      // exists to remove, reintroduced by a check meant to narrow it. Neither
      // expresses control flow, which is what this screen is actually for.
      //
      // A KNOWN call carrying the same extra key still passes today, because
      // `toToolCall` discards unrecognised fields before `.strict()` ever sees
      // them — a separate, pre-existing defect tracked on its own ticket. That
      // asymmetry is deliberate and temporary: this branch matches the DECLARED
      // contract, and the known path stops diverging from it when that lands.
      if (Object.keys(call).some((key) => !AUTHORABLE_CALL_FIELDS.has(key))) return false;
      const parameters = call.parameters ?? call.params;
      return parameters === undefined || isRecord(parameters);
    }

    // An `immediate` tool may not declare branches at all (ADR-046 Rule 14),
    // and that rule lives in `toolCallSchema`'s refinement — which the branch
    // strip below would skip. Checking it here first is what stops an immediate
    // call with an `onSuccess` full of unknown tools being reported as merely
    // unknown and having its whole binding deleted.
    if (
      isToolName(name) &&
      TOOL_RUN_MODES[name] === 'immediate' &&
      (call.onSuccess ?? call.onError) !== undefined
    ) {
      return false;
    }

    // Reduce the authored call to what `toolCallSchema` actually models before
    // asking it. Everything else on an authored binding is BRIDGE-ONLY —
    // `confirm` and `preventDefault` are lifted onto the dispatch binding by
    // `normaliseEventBinding`, `action` is an accepted spelling of `tool_name`,
    // `params` of `parameters`, and the branches are validated recursively
    // above. The schema is `.strict()`, so leaving any of them in makes it
    // reject a call the runtime handles perfectly well.
    //
    // Getting this wrong had a specific and bad shape: a KNOWN parent carrying
    // `confirm` (or authored via `action:`) with an unknown-tool child returned
    // "not well formed", so no unknown name was reported, the binding was not
    // dropped, and strict validation then failed the WHOLE surface — the
    // document-level fallback this pass exists to prevent, triggered by a valid
    // authored shape. The unknown-name branch above had already been corrected
    // for the same fields; this one had not, which is why both now read from
    // one list instead of each keeping its own.
    const bare = Object.fromEntries(
      Object.entries(call).filter(([key]) => SCHEMA_MODELLED_CALL_FIELDS.has(key)),
    );
    return toolCallSchema.safeParse({
      ...bare,
      tool_name: name,
      parameters: call.parameters ?? call.params ?? {},
    }).success;
  };

  return authored.every(wellFormedCall);
}

function normaliseElement(element: unknown): unknown {
  if (!isRecord(element)) return element;
  let result = element;

  const on = element.on;
  if (isRecord(on)) {
    const normalisedOn = Object.fromEntries(
      Object.entries(on).map(([eventName, binding]) => [eventName, normaliseEventBinding(binding)]),
    );
    result = { ...result, on: normalisedOn };
  }

  // `watch` bindings share the exact `{ tool_name, parameters }` authoring
  // shape as `on` — normalize them identically so the renderer's own action
  // dispatch (which reads `.action`/`.params`, not our `tool_name`/
  // `parameters` DSL) can execute them the same way.
  const watch = element.watch;
  if (isRecord(watch)) {
    const normalisedWatch = Object.fromEntries(
      Object.entries(watch).map(([statePath, binding]) => [
        statePath,
        normaliseEventBinding(binding),
      ]),
    );
    result = { ...result, watch: normalisedWatch };
  }

  return result;
}

function normaliseEventBinding(binding: unknown): unknown {
  const tools = toolChainForEventBinding(binding);
  if (!tools) return binding;

  const dispatchBinding: JsonRenderToolBinding = {
    action: JSON_RENDER_TOOL_CHAIN_HANDLER,
    params: {
      tools,
    },
  };

  const bindings = bindingsForEvent(binding);
  if (bindings.some((item) => isRecord(item) && item.preventDefault)) {
    dispatchBinding.preventDefault = true;
  }

  const confirmedBinding = bindings.find(
    (item): item is JsonRenderToolBinding =>
      isGalapagosToolBinding(item) && item.confirm !== undefined,
  );
  if (confirmedBinding) {
    copyOptionalBindingField(confirmedBinding, dispatchBinding, 'confirm');
  }

  return dispatchBinding;
}

/**
 * Is this event a Galapagos tool binding at all, as opposed to a renderer-owned
 * one? Cheap, and read from the AUTHORED bindings — extracted so normalization,
 * validation and the tolerance scan cannot drift on the answer.
 */
function isToolChainBinding(bindings: readonly unknown[]): boolean {
  return (
    bindings.length > 0 &&
    bindings.some(
      (item) =>
        isGalapagosToolBinding(item) || (isRecord(item) && typeof item.tool_name === 'string'),
    )
  );
}

function toolChainForEventBinding(binding: unknown): unknown[] | null {
  const bindings = bindingsForEvent(binding);
  if (!isToolChainBinding(bindings)) return null;
  const converted = new WeakMap<object, unknown>();
  return bindings.map((item) => toToolCall(item, converted));
}

function bindingsForEvent(binding: unknown): unknown[] {
  return Array.isArray(binding) ? binding : [binding];
}

export function toolsFromDispatchParams(params: unknown): unknown[] {
  if (!isRecord(params) || params.tools === undefined) return [];
  return Array.isArray(params.tools) ? params.tools : [params.tools];
}

/**
 * Convert one authored binding to the normalized `{ tool_name, parameters }`
 * shape, memoised by source object identity.
 *
 * The memo is what makes a CYCLIC branch tree survivable. A document whose
 * `onSuccess` reaches back to an ancestor call used to recurse here until the
 * stack blew — `extractJsonRenderToolChains` normalises before
 * `inspectToolChainDepth` gets a chance to report the cycle, so the guard meant
 * to reject the document was unreachable and the throw escaped instead
 * (`validateCatalogSpec` threw `RangeError` rather than returning a failure;
 * only the mobile host's outer try/catch turned that into a degraded render).
 *
 * Registering the target BEFORE recursing means a revisited source resolves to
 * the same target object, so the conversion terminates and reproduces the cycle
 * in its output — which is exactly what `inspectToolChainDepth`'s active-set
 * guard needs to see to report `cycle`. Breaking the cycle here instead would
 * be worse than the crash: a truncated finite chain passes the depth check, and
 * a cyclic document would validate.
 *
 * The tolerance scan no longer depends on any of this — it screens the authored
 * binding and never converts. The memo stays because the two conversion callers
 * still convert first and inspect afterwards, which is precisely the ordering
 * that produced the original crash; the alternative fix is to make every caller
 * inspect before converting, which is a wider change than this slice.
 */
function toToolCall(binding: unknown, converted: WeakMap<object, unknown>, depth = 0): unknown {
  if (!isRecord(binding)) return binding;

  const memoised = converted.get(binding);
  if (memoised) return memoised;

  const toolName = getToolName(binding);
  const toolCall: Record<string, unknown> = {
    tool_name: toolName,
    parameters: binding.parameters ?? binding.params ?? {},
  };
  converted.set(binding, toolCall);

  // Stop one level past the cap. The memo makes a CYCLIC tree finite but says
  // nothing about a merely very deep one, and an author-controlled 20k-level
  // binding would recurse here until the stack blew — before
  // `inspectToolChainDepth` ever ran, so the over-depth rejection it exists to
  // produce was again unreachable. Truncating one level beyond the cap keeps the
  // converted tree deep enough for the inspector to see `max_depth` and reject.
  if (depth <= MAX_TOOL_CHAIN_DEPTH) {
    copyOptionalBranch(binding, toolCall, 'onSuccess', converted, depth);
    copyOptionalBranch(binding, toolCall, 'onError', converted, depth);
  }

  return toolCall;
}

function isGalapagosToolBinding(value: unknown): value is JsonRenderToolBinding {
  if (!isRecord(value)) return false;
  const tool = getToolName(value);
  return tool !== undefined && isToolName(tool);
}

function getToolName(binding: Record<string, unknown>): ToolName | string | undefined {
  if (typeof binding.tool_name === 'string') return binding.tool_name;
  if (typeof binding.action === 'string') return binding.action;
  return undefined;
}

function copyOptionalBindingField(
  source: JsonRenderToolBinding,
  target: JsonRenderToolBinding,
  field: 'confirm',
) {
  if (source[field] !== undefined) {
    target[field] = source[field];
  }
}

function copyOptionalBranch(
  source: Record<string, unknown>,
  target: Record<string, unknown>,
  field: 'onSuccess' | 'onError',
  converted: WeakMap<object, unknown>,
  depth: number,
) {
  const branch: unknown = source[field];
  if (branch === undefined) return;
  if (!Array.isArray(branch)) {
    target[field] = branch;
    return;
  }
  // `Array.isArray` narrows to `any[]`, which makes a direct `.map` an unsafe
  // any-return; the explicit element annotation is what keeps it typed.
  target[field] = branch.map((item: unknown) =>
    isRecord(item) ? toToolCall(item, converted, depth + 1) : item,
  );
}

function toolTreeInspectionMessage(
  inspection: Exclude<ReturnType<typeof inspectToolChainDepth>, { ok: true }>,
): string {
  if (inspection.reason === 'cycle') return 'cannot contain circular branches';
  return `cannot exceed ${MAX_TOOL_CHAIN_DEPTH} nested branch levels`;
}
